import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings/`);
      if (response.data.success) {
        setBookings(response.data.bookings);
      }
    } catch (err) {
      setError('Failed to fetch bookings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'contacted':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getPackagePrice = (pkg) => {
    const prices = {
      starter: '₹1,499',
      pro: '₹2,199',
      transformation: '₹4,999'
    };
    return prices[pkg] || pkg;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#e8ff47] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] py-20 px-5 md:px-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-['Bebas_Neue'] text-[#e8ff47] text-5xl md:text-7xl mb-4">
            BOOKING MANAGEMENT
          </h1>
          <p className="font-['DM_Sans'] text-[#888680] text-lg">
            Total Bookings: {bookings.length}
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
            <p className="font-['DM_Sans'] text-red-500">{error}</p>
          </div>
        )}

        {/* Bookings Grid */}
        {bookings.length === 0 ? (
          <Card className="bg-[#302f2c] border-[#3f4816]">
            <CardContent className="p-12 text-center">
              <p className="font-['DM_Sans'] text-[#888680] text-lg">
                No bookings yet. Waiting for first customer!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bookings.map((booking) => (
              <Card 
                key={booking.id}
                className="bg-[#302f2c] border-[#3f4816] hover:bg-[#3f4816] transition-all"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-['Bebas_Neue'] text-[#e8ff47] text-2xl">
                        {booking.fullName}
                      </CardTitle>
                      <p className="font-['DM_Sans'] text-[#888680] text-sm mt-1">
                        {new Date(booking.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <Badge className={`${getStatusColor(booking.status)} text-white font-['DM_Sans'] uppercase text-xs px-3 py-1`}>
                      {booking.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="font-['DM_Sans'] text-[#888680] text-xs uppercase tracking-wider">
                      Contact
                    </p>
                    <p className="font-['DM_Sans'] text-[#dfddd6] text-base">
                      {booking.phone}
                    </p>
                  </div>

                  <div>
                    <p className="font-['DM_Sans'] text-[#888680] text-xs uppercase tracking-wider">
                      Gym
                    </p>
                    <p className="font-['DM_Sans'] text-[#dfddd6] text-base">
                      {booking.gym}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-['DM_Sans'] text-[#888680] text-xs uppercase tracking-wider">
                        Package
                      </p>
                      <p className="font-['DM_Sans'] text-[#e8ff47] text-base font-semibold capitalize">
                        {booking.package}
                      </p>
                      <p className="font-['DM_Sans'] text-[#888680] text-xs">
                        {getPackagePrice(booking.package)}
                      </p>
                    </div>

                    <div>
                      <p className="font-['DM_Sans'] text-[#888680] text-xs uppercase tracking-wider">
                        Shoot Date
                      </p>
                      <p className="font-['DM_Sans'] text-[#dfddd6] text-base">
                        {new Date(booking.date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {booking.message && (
                    <div>
                      <p className="font-['DM_Sans'] text-[#888680] text-xs uppercase tracking-wider">
                        Message
                      </p>
                      <p className="font-['DM_Sans'] text-[#dfddd6] text-sm italic">
                        "{booking.message}"
                      </p>
                    </div>
                  )}

                  <div className="pt-2">
                    <a 
                      href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER?.replace('+', '')}?text=Hi%20${encodeURIComponent(booking.fullName)},%20regarding%20your%20${booking.package}%20package%20booking`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-['DM_Sans'] text-[#25D366] text-sm hover:text-[#1eb855] transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Contact on WhatsApp
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
